import { useMemo, type ReactNode } from 'react';
import StatementPositiveSolid from '@simplywallst/ui-core/icons/StatementPositiveSolid';
import StatementNegativeSolid from '@simplywallst/ui-core/icons/StatementNegativeSolid';
import StatementNoDataSolid from '@simplywallst/ui-core/icons/StatementNoDataSolid';
import SupportSolid from '@simplywallst/ui-core/icons/SupportSolid';
import {
  StatementWrapper,
  StatementAnchor,
  StatementTitle,
} from '@/components/Statement/styled';
import type { Statement as StatementType } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import { Grid, Text, useTheme } from '@simplywallst/ui-core';
import { useLocale } from '@/hooks/useLocale';
import { useI18nFeedback } from '@/hooks/useI18nFeedback';
import useUser from '@/hooks/useUser';

//TODO @ric: remove ReduxValue types once Redux statements is completely removed from the Report
type ReduxValue = 'Pass' | 'Fail' | 'No data' | 'UnInitialised';

export type StatementValue = ReduxValue | StatementType['value'];

export interface Props {
  value?: StatementValue;
  title?: string;
  description?: ReactNode;
  statementId?: string;
  noIcon?: boolean;
  size?: 'small' | 'default';
}

const Statement = ({
  value,
  title,
  description,
  statementId,
  noIcon,
  size = 'default',
}: Props) => {
  const locale = useLocale();
  const theme = useTheme();

  const makeFeedbackDrawer = useI18nFeedback();
  const user = useUser();

  const statementIcon = useMemo(() => {
    switch (value) {
      case true:
      case 'Pass':
        return (
          <StatementPositiveSolid data-cy-id="pass" fill={theme.color.good} />
        );
      case false:
      case 'Fail':
        return (
          <StatementNegativeSolid data-cy-id="fail" fill={theme.color.bad} />
        );
      case null:
      case 'No data':
        return (
          <StatementNoDataSolid data-cy-id="no-data" fill={theme.color.bad} />
        );
      default:
        return null;
    }
  }, [value]);
  return (
    <StatementWrapper
      value={value}
      noIcon={noIcon}
      size={size}
      data-cy-id={`report-statement-${statementId}`}
    >
      <StatementAnchor id={statementId} />
      <Grid gap="16px" placeContent="center" templateColumns="16px 1fr">
        <div className="grid">{!noIcon && statementIcon}</div>
        <div className="grid">
          <Text typography="small">
            {title && (
              <StatementTitle value={value}>
                {title}
                {description && ': '}
              </StatementTitle>
            )}
            {description}
            {locale !== 'en' && user.data.loggedIn && (
              <Text as="span">
                <SupportSolid
                  fill={theme.color.textSubtle}
                  cursor="pointer"
                  onClick={makeFeedbackDrawer}
                />
              </Text>
            )}
          </Text>
        </div>
      </Grid>
    </StatementWrapper>
  );
};

export default Statement;
